import {
  agentDmReadState,
  channelState,
  dmState,
  readCursorState,
  unreadFlagState,
  workspaceState,
} from "../core/state.svelte.js";
import { reportUiFailure } from "../report-failure.js";
import { serverReadAhead } from "./unread-divider-refreeze.js";

// Reconnect seed for the ACTIVE workspace. The open conversation's frozen divider
// moves only when the server is ahead of the cursor this device already knew: its
// own reads are already in the local map (every markRead carries the same stamp
// it advanced locally), so that gap is a read on another device (#514).
export function reseedReadCursors(
  u: {
    reads?: Record<string, number> | null;
    dmReads?: Record<string, number> | null;
  },
  seededAt: number,
): void {
  const channelId = channelState.activeId;
  const peerId = dmState.activePeerId;
  const knownChannel = channelId ? readCursorState.getChannel(channelId) : null;
  const knownDm = peerId ? readCursorState.getDm(peerId) : null;
  const agentPeerId = agentDmReadState.peerId;
  const knownAgentDm = agentPeerId ? readCursorState.getDm(agentPeerId) : null;
  readCursorState.seed({
    reads: u.reads,
    dmReads: u.dmReads,
    wsId: workspaceState.current?.id,
    seededAt,
  });
  const channelAhead = channelId ? serverReadAhead(knownChannel, u.reads?.[channelId]) : undefined;
  if (channelAhead !== undefined) channelState.advanceUnreadCursor(channelAhead);
  const dmAhead = peerId ? serverReadAhead(knownDm, u.dmReads?.[peerId]) : undefined;
  if (dmAhead !== undefined) dmState.advanceUnreadCursor(dmAhead);
  const agentAhead = agentPeerId
    ? serverReadAhead(knownAgentDm, u.dmReads?.[agentPeerId])
    : undefined;
  if (agentPeerId && agentAhead !== undefined)
    agentDmReadState.advanceUnreadCursor(agentPeerId, agentAhead);
}

/** A mark-unread on my other device (read_broadcast `rewind: true`): lower the cursor, bold the channel here too. */
export function applyReadRewind(
  p: { workspaceId: string; channelId: string; lastReadAt: number },
  reconcile: (wsId: string) => Promise<void>,
): void {
  // Same gate as the DM path: a background workspace never writes the open workspace's cursor map.
  if (workspaceState.current?.id === p.workspaceId)
    readCursorState.setChannel(p.channelId, p.lastReadAt);
  // No message arrived: flag without +1, the reseed brings the real number.
  unreadFlagState.markUnread(p.workspaceId, p.channelId, false);
  if (workspaceState.current?.id === p.workspaceId && p.channelId === channelState.activeId)
    channelState.setUnreadCursor(p.lastReadAt);
  const wsId = p.workspaceId;
  reconcile(wsId).catch((e) =>
    reportUiFailure("unread.rewind_reconcile", e, { workspaceId: wsId }),
  );
}
